import React, { useMemo } from "react";
import "./campaign-view.css";

function formatMoney(n, currency = "CLP", locale = "es-CL") {
  try {
    return new Intl.NumberFormat(locale, { style: "currency", currency, maximumFractionDigits: 0 }).format(n || 0);
  } catch {
    return new Intl.NumberFormat(locale).format(n || 0);
  }
}
function formatDate(d) {
  if (!d) return "-";
  const date = new Date(d);
  return new Intl.DateTimeFormat("es-CL", { dateStyle: "medium", timeStyle: "short" }).format(date);
}

/**
 * Resuelve la URL de la imagen:
 * - string directo: se usa tal cual
 * - objeto: usa .url o .Location
 * - key: si viene { key: "folder/uuid.jpg" } y pasas imageBase (p.ej. https://s3.us-east-1.wasabisys.com/tu-bucket)
 *         construye imageBase + "/" + key
 */
function getImageSrc(img, imageBase) {
  if (!img) return "";
  if (typeof img === "string") return img;
  if (img.url) return img.url;
  if (img.Location) return img.Location;
  if (img.key && imageBase) return `${imageBase.replace(/\/+$/, "")}/${img.key}`;
  return "";
}

export default function CampaignView({ campaign, imageBase }) {
  const donations = campaign?.donations || [];
  const { total, approvedTotal, approvedCount, progressPct } = useMemo(() => {
    const sumAll = donations.reduce((acc, d) => acc + (Number(d.amount) || 0), 0);
    const approved = donations.filter(d => d.status === "approved");
    const sumApproved = approved.reduce((acc, d) => acc + (Number(d.amount) || 0), 0);
    const pct = campaign?.meta ? (sumApproved / campaign.meta) * 100 : 0;
    return { total: sumAll, approvedTotal: sumApproved, approvedCount: approved.length, progressPct: pct };
  }, [donations, campaign?.meta]);

  const status = (campaign?.status || "pending").toLowerCase();
  const progressWidth = Math.min(100, progressPct).toFixed(2);
  const imgSrc = getImageSrc(campaign?.img, imageBase);

  return (
    <div className="cv-card" role="region" aria-label="Campaña">
      <header className="cv-header">
        <div className="cv-header__left">
          <h2 className="cv-title">{campaign?.title || "Campaña"}</h2>
          {campaign?.description && <p className="cv-description">{campaign.description}</p>}
        </div>
        <span className={`cv-badge ${status === "active" ? "is-active" : "is-inactive"}`}>
          {status === "active" ? "Activa" : (campaign?.status || "Sin estado")}
        </span>
      </header>

      {/* Imagen de la campaña (opcional) */}
      {imgSrc && (
        <figure className="cv-media">
          <img
            src={imgSrc}
            alt={`Imagen de la campaña: ${campaign?.title || ""}`}
            loading="lazy"
            decoding="async"
          />
        </figure>
      )}

      <section className="cv-stats" aria-label="Estadísticas">
        <div className="cv-stat">
          <div className="cv-stat__label">Meta</div>
          <div className="cv-stat__value">{formatMoney(campaign?.meta)}</div>
        </div>
        <div className="cv-stat">
          <div className="cv-stat__label">Recaudado (aprobado)</div>
          <div className="cv-stat__value">{formatMoney(approvedTotal)}</div>
        </div>
        <div className="cv-stat">
          <div className="cv-stat__label">Donaciones aprobadas</div>
          <div className="cv-stat__value">{approvedCount}</div>
        </div>
        <div className="cv-stat">
          <div className="cv-stat__label">Creada</div>
          <div className="cv-stat__value">{formatDate(campaign?.createdAt)}</div>
        </div>
      </section>

      <section className="cv-progress" aria-label="Progreso hacia la meta">
        <div className="cv-progress__label">Progreso</div>
        <div className="cv-progress__bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Number(progressWidth)}>
          <div className="cv-progress__fill" style={{ width: `${progressWidth}%` }} />
        </div>
        <div className="cv-progress__text">
          {formatMoney(approvedTotal)} de {formatMoney(campaign?.meta)} ({Number(progressWidth).toFixed(1)}%)
        </div>
      </section>

      <section className="cv-row">
        <h3 className="cv-section-title">Donaciones</h3>
        <div className="cv-table-wrap">
          {donations.length === 0 ? (
            <div className="cv-empty">No hay donaciones aún.</div>
          ) : (
            <table className="cv-table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Monto</th>
                  <th>Estado</th>
                  <th>Referencia</th>
                  <th>Fecha</th>
                  <th>Comentario</th>
                </tr>
              </thead>
              <tbody>
                {donations.map((d) => {
                  const chipClass =
                    d.status === "approved" ? "cv-chip is-approved" :
                    d.status === "rejected" ? "cv-chip is-rejected" :
                    "cv-chip is-pending";
                  return (
                    <tr key={d._id || d.reference}>
                      <td>{[d.name, d.lastname].filter(Boolean).join(" ") || "-"}</td>
                      <td>{formatMoney(d.amount)}</td>
                      <td><span className={chipClass}>{d.status || "pending"}</span></td>
                      <td>{d.reference || "-"}</td>
                      <td>{formatDate(d.createdAt)}</td>
                      <td>{d.isComment ? (d.comment || "—") : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}